import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AnalyticsProfile } from '@app/pages/shared/interfaces/viewer-profile.interface';
import * as clevertap from 'clevertap-web-sdk';
import { environment } from '@environments/environment';
import { AnalyticsPreferencesPayload, EventParams } from '@app/core/interfaces';
import { AuthService } from '../auth.service';

type Region = 'sg1' | 'in1' | 'us1' | 'aps3' | 'mec1' | 'eu1' | 'sk1'; // clevertap regions
@Injectable({
  providedIn: 'root',
})
export class CleverTapService {
  private isInitialized = false;
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly authService: AuthService
  ) {}

  private readonly accountId = environment.cleverTap.accountID;
  private readonly region = (environment.cleverTap?.region ?? 'eu1') as Region;

  private page: string | null = null;
  private dialog: string | null = null;

  initializeCleverTap() {
    if (!isPlatformBrowser(this.platformId) || this.isInitialized || !this.authService.isLoggedIn.value) {
      return;
    }
    try {
      clevertap.init(this.accountId, this.region);
      clevertap.privacy.push({ optOut: false, useIP: true });
      (clevertap.spa as any) = true;
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize CleverTap:', error);
    }
  }

  private isCleverTapAvailable(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      !!clevertap &&
      !!clevertap.getCleverTapID()
    );
  }

  /**
   * Formats a key by splitting camelCase and capitalizing the first letter of each word.
   * For example, 'isWhatsApp' becomes 'Is WhatsApp'.
   * @param key The key to format.
   * @returns The formatted key.
   */
  private formatKey(key: string): string {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter
  }

  /**
   * Transforms the keys of the params object to a more readable format.
   * For example, 'isWhatsApp' becomes 'Is WhatsApp', and camelCase keys are split into words.
   * @param params The parameters object to transform.
   * @returns A new object with transformed keys.
   */
  private transformParams(params: any): any {
    if (!params || typeof params !== 'object') return params;

    return Object.entries(params).reduce((acc, [key, value]) => {
      let formattedKey =
        key === 'isWhatsApp' ? 'Is WhatsApp' : this.formatKey(key);
      acc[formattedKey] =
        typeof value === 'object' ? this.transformParams(value) : value;
      return acc;
    }, {} as any);
  }

  pageOpenEvent(params: EventParams, isDialog: boolean): void {
    let transformed = this.transformParams(params);
    let eventName;
    if (isDialog) {
      eventName = 'Dialog Open';
      this.dialog = params?.page ?? '';
    } else {
      eventName = 'Page Open';
      this.page = params?.page ?? '';
    }
    this.pushEvent(
      () => clevertap.event.push(eventName, transformed),
      'CleverTap page open event error'
    );
  }

  pageExit(isDialog: boolean) {
    let eventName = isDialog ? 'Dialog Close' : 'Page Close';
    this.pushEvent(
      () =>
        clevertap.event.push(eventName, {
          page: isDialog ? this.dialog : this.page,
        }),
      'CleverTap page exit event error'
    );
  }

  onLogin(user: AnalyticsProfile): void {
    let transformed = this.transformParams(user);
    this.pushEvent(
      () => clevertap?.onUserLogin.push({ Site: transformed }),
      'CleverTap on login error'
    );
  }

  onUpdateProfile(profile: AnalyticsProfile): void {
    let transformed = this.transformParams(profile);
    this.pushEvent(
      () => clevertap?.profile.push({ Site: transformed }),
      'CleverTap profile update error'
    );
  }

  customEvent(eventName: string, params: EventParams): void {
    let transformed = this.transformParams(params);
    this.pushEvent(
      () => clevertap?.event.push(eventName, transformed),
      'CleverTap custom event error'
    );
  }

  onPreferencesUpdate(
    preferences: AnalyticsPreferencesPayload,
    page: string
  ): void {
    this.pushEvent(
      () => clevertap?.profile.push({ Site: preferences }),
      'CleverTap preferences update error'
    );
  }

  failureEvent(failEventName: string, eventParams: EventParams) {
    let transformed = this.transformParams(eventParams);
    this.pushEvent(
      () => clevertap.event.push(failEventName, transformed),
      'CleverTap failure event error'
    );
  }

  private pushEvent(action: () => void, errorMessage: string): void {
    if (!this.isCleverTapAvailable()) {
      console.warn('CleverTap is unavailable, skipping event push');
      return;
    }

    try {
      if (
        isPlatformBrowser(this.platformId) &&
        this.authService.isLoggedIn.value
      ) {
        action();
        console.log('😃 action pushed...');
      }
    } catch (error) {
      console.error(errorMessage, error);
    }
  }
}
